import { useCallback, useEffect, useState } from 'react'
import { useField, useFormikContext } from 'formik'

import type { Time } from '../typings'
import { convertTimeInput } from '../utils'
import {
  AutocompleteChangeReason,
  AutocompleteInputChangeReason,
} from '@mui/material/Autocomplete'
import { addMinutes, startOfToday } from 'date-fns/esm'

type Params = {
  name: string
  value: Time
}

export function useActions(params: Params) {
  const { name } = params

  const { values, setFieldValue } = useFormikContext()
  const [, , { setTouched }] = useField(name)

  const [{ inputValue, updateOnBlur }, setState] = useState(() => ({
    inputValue: '',
    updateOnBlur: false,
  }))

  useEffect(() => {
    if (!values || !(values as any)[name]) {
      setState({
        inputValue: '',
        updateOnBlur: false,
      })
    }
  }, [name, values])

  const handleUserInput = useCallback(
    (userInput: string) => {
      const minutes = convertTimeInput(userInput, 'hh:mm aa')

      if (minutes !== 'invalid') {
        setFieldValue(name, addMinutes(startOfToday(), minutes))
      } else {
        setFieldValue(name, userInput)
      }
    },
    [name, setFieldValue],
  )

  const onChange = useCallback(
    (_e, option: Time | string, reason: AutocompleteChangeReason) => {
      // option always be string when creating, keep as typeguard
      if (reason === 'createOption' && typeof option === 'string') {
        handleUserInput(option)
      } else {
        setFieldValue(name, option)
      }
    },
    [handleUserInput, name, setFieldValue],
  )

  const onInputChange = useCallback(
    (_, value: string, reason: AutocompleteInputChangeReason) =>
      setState({
        inputValue: value,
        updateOnBlur: reason === 'input',
      }),
    [],
  )

  const onBlur = useCallback(() => {
    if (updateOnBlur) handleUserInput(inputValue)
    setTouched(true)
  }, [handleUserInput, inputValue, updateOnBlur])

  return {
    onBlur,
    onChange,
    inputValue,
    onInputChange,
  }
}
